import type { AgentComponent, AgentComponentType } from '@reach/shared-types/agent-components';
import { Plus } from 'lucide-react';
import { useId, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { COMPONENT_TYPE_COPY } from '../agents-copy';
import { useTemplateComponentMutations } from '../useTemplateComponents';
import { AddComponentDialog } from './AddComponentDialog';
import { TemplateComponentItem } from './TemplateComponentItem';
import styles from './TemplateSection.module.css';

interface TemplateSectionProps {
  templateId: string;
  type: AgentComponentType;
  components: AgentComponent[];
}

export const TemplateSection = ({ templateId, type, components }: TemplateSectionProps) => {
  const headingId = useId();
  const [addingComponent, setAddingComponent] = useState(false);
  const { addComponents, removeComponent } = useTemplateComponentMutations(templateId);
  const { title, addLabel, templateDescription } = COMPONENT_TYPE_COPY[type];
  const addHeading = `${addLabel} to Voice template`;
  const sectionIsEmpty = components.length === 0;

  const closeAddDialog = () => setAddingComponent(false);

  const handleAdd = (componentIds: string[]) => {
    addComponents.mutate(componentIds);
    closeAddDialog();
  };

  return (
    <section className={styles.templateSection} aria-labelledby={headingId}>
      <header>
        <h2 id={headingId}>{title}</h2>
        <p>{templateDescription}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={addComponents.isPending}
          onClick={() => setAddingComponent(true)}>
          <Plus aria-hidden="true" />
          {addLabel}
        </Button>
      </header>
      {addingComponent && (
        <AddComponentDialog
          type={type}
          heading={addHeading}
          templateComponentIds={components.map(component => component.id)}
          onAdd={handleAdd}
          onClose={closeAddDialog}
        />
      )}
      {sectionIsEmpty ? (
        <p>None on this template yet.</p>
      ) : (
        <ul>
          {components.map(component => (
            <TemplateComponentItem
              key={component.id}
              component={component}
              isRemoving={removeComponent.isPending}
              onRemove={() => removeComponent.mutate(component.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
};
